'use client'

import React, {
	createContext,
	useContext,
	useState,
	useMemo,
	ReactNode,
} from 'react'
import {
	BenchmarkScale,
	BenchmarkScaleParam,
} from '@/lib/enums/bi-enums'
import {
	analyticsTags,
	radioAnalyticsEvents,
} from '@/app/lib/analytics/analyticsEventTags'
import { pushToDataLayer } from '@/app/lib/analytics/analyticsTracking'
import useResolvedBenchmarkScale, {
	benchmarkMap,
	benchmarkParamToScaleMap,
} from '@/lib/hooks/useResolvedBenchmarkScale'
import { usePreferences } from '@/lib/context/preferencesContext'
import { useCompanyContext } from './companyContext'

interface BenchmarkContextType {
	isBenchmarkChecked: boolean
	benchmarkRadioValue: BenchmarkScale
	benchmarkScale: BenchmarkScaleParam | undefined
	openDialog: boolean
	hasOpenedDialog: boolean
	setOpenDialog: (isOpen: boolean) => void
	dialogCloseHandler: () => void
	dialogRadioCalculation: (
		wasOpened: boolean,
		peerGroupBenchmark?: BenchmarkScaleParam
	) => BenchmarkScale
	dialogApplyChangesHandler: (
		isChecked: boolean,
		radioValue: BenchmarkScale
	) => void
}

const BenchmarkContext = createContext<BenchmarkContextType | undefined>(
	undefined
)

export const BenchmarkProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { selectedCompanyId: companyId } = useCompanyContext()

	const { busIntelPreferences, updateBusIntelPreferences } = usePreferences()

	const isBenchmarkChecked = useMemo(() => {
		const gciCategoryCd =
			busIntelPreferences?.preferences?.performance?.benchmark?.gciCategoryCd
		if (gciCategoryCd === undefined) return true // default when no preferences
		return gciCategoryCd !== BenchmarkScaleParam.Unknown
	}, [busIntelPreferences])

	const benchmarkRadioValue = useMemo(() => {
		const gciCategoryCd =
			busIntelPreferences?.preferences?.performance?.benchmark?.gciCategoryCd
		if (!gciCategoryCd || gciCategoryCd === BenchmarkScaleParam.Unknown) {
			return BenchmarkScale.Unknown
		}
		return (
			benchmarkParamToScaleMap[gciCategoryCd as BenchmarkScaleParam] ??
			BenchmarkScale.Unknown
		)
	}, [busIntelPreferences])

	const [openDialog, setOpenDialog] = useState(false)
	const [hasOpenedDialog, setHasOpenedDialog] = useState(false)

	const benchmarkScale = useResolvedBenchmarkScale(benchmarkRadioValue)

	const { benchmarkChecked, benchmarkUnchecked } = analyticsTags

	const dialogCloseHandler = React.useCallback(() => {
		setOpenDialog(false)
	}, [])

	const dialogApplyChangesHandler = React.useCallback(
		(isChecked: boolean, radioValue: BenchmarkScale) => {
			if (isChecked && radioValue in radioAnalyticsEvents) {
				pushToDataLayer(
					radioAnalyticsEvents[radioValue as keyof typeof radioAnalyticsEvents]
				)
			}
			pushToDataLayer(isChecked ? benchmarkChecked : benchmarkUnchecked)

			const gciCategoryCd = isChecked
				? benchmarkMap[radioValue].toString()
				: benchmarkMap.unknown.toString()

			updateBusIntelPreferences({
				...busIntelPreferences!,
				companyId: companyId ?? undefined,
				preferences: {
					...busIntelPreferences?.preferences,
					performance: {
						...busIntelPreferences?.preferences?.performance,
						benchmark: {
							...busIntelPreferences?.preferences?.performance?.benchmark,
							gciCategoryCd,
							showBenchmarkFl: isChecked,
						},
					},
				},
			})

			setOpenDialog(false)
			setHasOpenedDialog(true)
		},
		[
			benchmarkChecked,
			benchmarkUnchecked,
			updateBusIntelPreferences,
			busIntelPreferences,
			companyId,
		]
	)

	const dialogRadioCalculation = React.useCallback(
		(
			wasOpened: boolean,
			peerGroupBenchmark?: BenchmarkScaleParam
		): BenchmarkScale => {
			if (
				busIntelPreferences?.preferences?.performance?.benchmark?.gciCategoryCd
			) {
				const storedBenchmark =
					busIntelPreferences.preferences.performance.benchmark.gciCategoryCd
				const mappedValue =
					benchmarkParamToScaleMap[storedBenchmark as BenchmarkScaleParam]
				if (mappedValue && mappedValue !== BenchmarkScale.Unknown) {
					return mappedValue
				}
				if (mappedValue && mappedValue === BenchmarkScale.Unknown) {
					if (wasOpened) {
						return benchmarkRadioValue
					}
					const defaultValue =
						benchmarkParamToScaleMap[peerGroupBenchmark as BenchmarkScaleParam]
					return defaultValue
				}
			}

			if (
				!busIntelPreferences?.preferences?.performance?.benchmark
					?.gciCategoryCd &&
				peerGroupBenchmark
			) {
				const defaultValue = benchmarkParamToScaleMap[peerGroupBenchmark]
				return defaultValue
			}

			return BenchmarkScale.Unknown
		},
		[benchmarkRadioValue, busIntelPreferences]
	)

	const value = React.useMemo(
		() => ({
			isBenchmarkChecked,
			benchmarkRadioValue,
			benchmarkScale,
			openDialog,
			hasOpenedDialog,
			setOpenDialog,
			dialogCloseHandler,
			dialogRadioCalculation,
			dialogApplyChangesHandler,
		}),
		[
			isBenchmarkChecked,
			benchmarkRadioValue,
			benchmarkScale,
			hasOpenedDialog,
			openDialog,
			dialogCloseHandler,
			dialogRadioCalculation,
			dialogApplyChangesHandler,
		]
	)

	return (
		<BenchmarkContext.Provider value={value}>
			{children}
		</BenchmarkContext.Provider>
	)
}

export const useBenchmark: () => BenchmarkContextType = () => {
	const context = useContext(BenchmarkContext)
	if (context === undefined) {
		throw new Error('useBenchmark must be used within a BenchmarkProvider')
	}
	return context
}
